'use client';

import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormSelect } from '@/components/forms/form-select';
import {
  createContainerTypeSchema,
  type ContainerTypeFormValues
} from '../schemas';
import {
  useCreateContainerTypeMutation,
  useUpdateContainerTypeMutation
} from '../services/container-types.mutations';
import type { ContainerType } from '../types';

interface ContainerTypeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  containerType?: ContainerType | null;
}

export function ContainerTypeFormDialog({
  open,
  onOpenChange,
  containerType
}: ContainerTypeFormDialogProps) {
  const t = useTranslations('containerTypes');
  const tv = useTranslations('validation');
  const isEditing = !!containerType;

  const schema = useMemo(() => createContainerTypeSchema(tv), [tv]);

  const form = useForm<ContainerTypeFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      status: 'active'
    }
  });

  const createMutation = useCreateContainerTypeMutation();
  const updateMutation = useUpdateContainerTypeMutation();
  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (open && containerType) {
      form.reset({
        name: containerType.name,
        status: containerType.status
      });
    } else if (open) {
      form.reset({
        name: '',
        status: 'active'
      });
    }
  }, [open, containerType, form]);

  async function onSubmit(values: ContainerTypeFormValues) {
    if (isEditing && containerType) {
      await updateMutation.mutateAsync({ id: containerType.id, data: values });
    } else {
      await createMutation.mutateAsync(values);
    }
    onOpenChange(false);
  }

  const statusOptions = [
    { value: 'active', label: t('statusActive') },
    { value: 'inactive', label: t('statusInactive') }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('editContainerType') : t('createContainerType')}
          </DialogTitle>
        </DialogHeader>

        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
          <div className='space-y-4'>
            <FormInput
              control={form.control}
              name='name'
              label={t('name')}
              placeholder={t('namePlaceholder')}
              required
            />

            <FormSelect
              control={form.control}
              name='status'
              label={t('status')}
              options={statusOptions}
              required
            />

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
              >
                {t('cancel')}
              </Button>
              <Button type='submit' disabled={isPending}>
                {isPending && <Loader2 className='me-2 h-4 w-4 animate-spin' />}
                {isEditing ? t('saveChanges') : t('create')}
              </Button>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
